import { HttpClient, HttpErrorResponse, HttpHandler, HttpHeaders, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BASIC_WEB_AUTH, AUTH_CLIENT_WEB, HEADER_TRACE_TOKEN, HEADER_CHALLENGE_TOKEN } from "app/const";
import { ToastrService } from "./toastr.service";
import { environment } from "environments/environment";
import { Observable, throwError } from "rxjs";
import { catchError, finalize, map, switchMap } from "rxjs/operators";
import { AuthService } from "app/shared/auth/auth.service";

interface PreconditionPayload {
  traceToken?: string;
  validationType?: string;
  additionalData?: any;
  message?: string;
}

@Injectable({ providedIn: "root" })
export class OperationValidationService {

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private toastr: ToastrService,
    private authService: AuthService
  ) { }

  handlePrecondition(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<any> {
    const payload = error?.error as PreconditionPayload;
    const traceToken = payload?.traceToken;
    const validationType = payload?.validationType;

    if (!traceToken || !validationType) {
      return throwError(() => error);
    }

    switch (validationType) {
      case "REQUIRED_OPERATION_ROLE":
        return this.handleRequiredOperationRole(req, next, traceToken, payload);
      default:
        return throwError(() => error);
    }
  }

  private handleRequiredOperationRole(
    req: HttpRequest<any>,
    next: HttpHandler,
    traceToken: string,
    _payload: PreconditionPayload
  ): Observable<any> {
    const senhaArmazenada = this.authService.getStoredPassword();
    if (!senhaArmazenada) {
      this.toastr.warning(this.translate.instant("VALIDACAO_OPERACAO_SENHA_NAO_DISPONIVEL"));
      return throwError(() => new HttpErrorResponse({
        status: 499,
        statusText: "Operation password not provided"
      }));
    }

    return this.revalidateUser(req, traceToken, senhaArmazenada).pipe(
      switchMap((challengeToken) => {
        const augmentedRequest = req.clone({
          headers: req.headers
            .set(HEADER_TRACE_TOKEN, traceToken)
            .set(HEADER_CHALLENGE_TOKEN, challengeToken)
        });
        return next.handle(augmentedRequest);
      }),
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 412) {
          const mensagem = err.error?.message || this.translate.instant("VALIDACAO_OPERACAO_SENHA_INVALIDA");
          this.toastr.warning(mensagem);
          return throwError(() => new HttpErrorResponse({
            status: 499,
            statusText: "Operation validation failed"
          }));
        }
        return throwError(() => err);
      })
    );
  }

  private revalidateUser(
    originalRequest: HttpRequest<any>,
    traceToken: string,
    senha: string
  ): Observable<string> {
    const headers = new HttpHeaders()
      .set("Authorization", BASIC_WEB_AUTH)
      .set("Content-Type", "application/json")
      .set(HEADER_TRACE_TOKEN, traceToken);

    const body = { senha };

    const baseUrl = this.resolveBaseUrl(originalRequest.url);
    const endpoint = `${baseUrl}/auth/${AUTH_CLIENT_WEB}/revalidate-user`;

    return this.http.post<{ challengeToken: string }>(
      endpoint,
      body,
      { headers }
    ).pipe(
      map((response) => response.challengeToken),
      finalize(() => this.authService.clearStoredPassword())
    );
  }

  private resolveBaseUrl(requestUrl: string): string {
    try {
      const { protocol, host } = new URL(requestUrl);
      if (protocol && host) {
        return `${protocol}//${host}`;
      }
    } catch (e) {
      // ignore and fallback
    }
    return environment.urlApi.replace(/\/$/, "");
  }
}
